import { UrlPrefixEnum } from '../types/cmsEnums';
import { PageType, VerticalType } from '../types/cmsTypes';
import { Brand, BrandId } from './override';

export const cmsQueryParamsPrefix = 'cms_';

export const APPLY_V3_URL_PREFIX = 'apply-v3';

export enum ApplyMode {
  // mode 1
  PDP = 'pdp',
  // mode 2
  Ads = 'ads',
  // mode 3
  Campaign = 'campaign',
}

/** Marks a vertical a brand does not serve at all. */
const NOT_AVAILABLE = Symbol('NOT_AVAILABLE');

/** A brand's own slug, its inbound-only aliases, or NOT_AVAILABLE. */
type BrandRoute =
  | string
  | { slug?: string; aliases?: string[] }
  | typeof NOT_AVAILABLE;

type VerticalRoute = { defaultSlug: string } & Partial<
  Record<BrandId, BrandRoute>
>;

type VerticalRoutes = Record<VerticalType, VerticalRoute>;

/**
 * The URL segment of each vertical, `/<slug>/…`; a brand key overrides the default for that brand.
 * Aliases resolve to the vertical but links never use them. The fe-services proxy claims every slug
 * and alias here as a CMS path, so a brand serving one elsewhere needs NOT_AVAILABLE or a
 * fe-services `CMS_EXCEPTIONS_URLS` entry.
 */
const VERTICAL_ROUTES: VerticalRoutes = {
  CREDIT_CARD: { defaultSlug: 'credit-card', money101: '信用卡' },
  PERSONAL_LOANS: {
    defaultSlug: 'personal-loan',
    money101: '貸款',
    moneyhero: { aliases: ['loans'] },
    moneymax: { aliases: ['loans'] },
  },
  TRAVEL_INSURANCE: { defaultSlug: 'travel-insurance' },
  CAR_INSURANCE: { defaultSlug: 'car-insurance' },
  INSURANCE: { defaultSlug: 'insurance' },
  BANKING: { defaultSlug: 'banking', money101: '銀行帳戶' },
  INVESTMENTS: {
    defaultSlug: 'investment',
    money101: '證券開戶',
    // Not on SingSaver: `/investments` is one of its funnels.
    moneyhero: { aliases: ['investments'] },
    moneymax: { aliases: ['investments'] },
  },
  TELCO: { defaultSlug: 'telco' },
  PERSONAL_FINANCE: { defaultSlug: 'personal-finance' },
  LIFESTYLE: { defaultSlug: 'lifestyle' },
  GOVERNMENT_SERVICES: { defaultSlug: 'government-services' },
  MONEY_MANAGEMENT: { defaultSlug: 'money-management' },
  SPONSORED: { defaultSlug: 'sponsored' },
  ASSET_MANAGEMENT: { defaultSlug: 'asset-management' },
  HOME_LOAN: { defaultSlug: 'home-loan' },
  E_WALLET: { defaultSlug: 'e-wallet' },
  OTHERS: { defaultSlug: 'others' },
  MOTORCYCLE_INSURANCE: { defaultSlug: 'motorcycle-insurance' },
  // MoneyHero serves /zh/life-insurance from HubSpot; Money101 would also need its VirtualService
  // to send these slugs to fe-service.
  SME: {
    defaultSlug: 'sme',
    money101: NOT_AVAILABLE,
    moneyhero: NOT_AVAILABLE,
    singsaver: NOT_AVAILABLE,
  },
  OFW: {
    defaultSlug: 'ofw',
    money101: NOT_AVAILABLE,
    moneyhero: NOT_AVAILABLE,
    singsaver: NOT_AVAILABLE,
  },
  LIFE_INSURANCE: {
    defaultSlug: 'life-insurance',
    money101: NOT_AVAILABLE,
    moneyhero: NOT_AVAILABLE,
    singsaver: NOT_AVAILABLE,
  },
  // MoneyHero and Moneymax serve these paths outside the CMS (/zh/home-insurance, /pet-insurance).
  // On SingSaver `home-insurance` and `maid-insurance` are also funnels: the slug stays because
  // `/home-insurance/blog/<slug>` is CMS, and fe-services' `CMS_EXCEPTIONS_URLS` keeps the funnel out.
  HEALTH_INSURANCE: {
    defaultSlug: 'health-insurance',
    moneyhero: NOT_AVAILABLE,
    moneymax: NOT_AVAILABLE,
  },
  PET_INSURANCE: {
    defaultSlug: 'pet-insurance',
    moneyhero: NOT_AVAILABLE,
    moneymax: NOT_AVAILABLE,
  },
  HOME_INSURANCE: {
    defaultSlug: 'home-insurance',
    moneyhero: NOT_AVAILABLE,
    moneymax: NOT_AVAILABLE,
  },
  MAID_INSURANCE: {
    defaultSlug: 'maid-insurance',
    moneyhero: NOT_AVAILABLE,
    moneymax: NOT_AVAILABLE,
  },
};

const resolveVerticalRoute = (
  vertical: VerticalType,
  brand: BrandId,
  routes: VerticalRoutes = VERTICAL_ROUTES,
): { slug: string; aliases: string[] } | undefined => {
  const route = routes[vertical];
  const own = route[brand];
  if (own === NOT_AVAILABLE) return undefined;
  if (typeof own === 'string') return { slug: own, aliases: [] };
  return { slug: own?.slug ?? route.defaultSlug, aliases: own?.aliases ?? [] };
};

/** Exported for tests; `routes` defaults to the real table. */
export const buildBrandMapping = (
  brand: BrandId,
  routes: VerticalRoutes = VERTICAL_ROUTES,
): Record<string, VerticalType> => {
  const mapping: Record<string, VerticalType> = {};
  for (const vertical of Object.keys(routes) as VerticalType[]) {
    const route = resolveVerticalRoute(vertical, brand, routes);
    for (const slug of route ? [route.slug, ...route.aliases] : []) {
      if (mapping[slug]) {
        throw new Error(
          `${brand}: /${slug} is both ${mapping[slug]} and ${vertical}`,
        );
      }
      mapping[slug] = vertical;
    }
  }
  return mapping;
};

/** Every slug and alias a brand resolves, with its vertical. */
export const VERTICAL_MAPPING_BY_BRAND: Brand<Record<string, VerticalType>> = {
  money101: buildBrandMapping('money101'),
  moneyhero: buildBrandMapping('moneyhero'),
  moneymax: buildBrandMapping('moneymax'),
  singsaver: buildBrandMapping('singsaver'),
};

/**
 * Which blog features each brand serves from this platform.
 *
 * One list used to answer three different questions — does the proxy own `/blog/tag/<slug>`, should
 * an article render tag chips, and should auto related-articles run. That held only while the answers
 * agreed. They stopped agreeing on 2 Sep: MoneyHero's articles moved onto this platform while its
 * `/blog/tag/<slug>` archive is still served by HubSpot and we hold no tag content for it, so
 * claiming that path would 404 a live indexed page. Two facts, two tables, each named for its own
 * question.
 *
 * Total `Record<BrandId, boolean>` on purpose: a new market does not compile until both answers are
 * given, rather than defaulting to `false` and going quiet.
 *
 * NOT the only place this knowledge lives: cms-service cannot import this package, so its MCP
 * article-URL reconstruction encodes the same thing separately
 * (`in-process-cms-catalog.gateway.ts`, BLOG_ONLY_VERTICALS + the money101 branch). Update both.
 */
export const BLOG_TAG_ARCHIVE_BY_BRAND: Record<BrandId, boolean> = {
  money101: true,
  // The migration carries tags: 619 of MoneyHero's 685 articles have them, 1,651 distinct tags, and
  // the one live indexed archive we checked — /blog/tag/汽車保險 — resolves to 5 of them here. So this
  // is `true` on the same grounds as the verticals: the content exists.
  //
  // It is safe before cutover because CloudFront still sends /blog/tag/* on moneyhero.com.hk to
  // HubSpot, so the flag decides nothing until the paths move; and it must be `true` by the moment
  // they do, or a live archive 404s in the window.
  moneyhero: true,
  moneymax: true,
  // Same grounds as MoneyHero: SingSaver's tag archive keeps its address, `/blog/tag/<slug>`, and
  // CloudFront sends `/blog/*` on singsaver.com.sg to HubSpot until cutover. Until then the flag
  // decides nothing; by the moment the paths move it must already be `true`, or every live indexed
  // archive 404s in the window between the CDN change and the next deploy.
  singsaver: true,
};

/**
 * Whether an article page computes auto related-articles for this brand. Separate from the tag
 * archive above: it needs migrated ARTICLES, not a tag archive, and it costs a CMS query per render.
 *
 * Every market is on. Where it shows is decided per article, not here: an article carrying a manual
 * `RelevantArticle` suppresses the auto section (`hasManualRelated` in `ArticleContent`), so in
 * practice this surfaces on imported articles, which have no such block.
 */
export const AUTO_RELATED_ARTICLES_BY_BRAND: Record<BrandId, boolean> = {
  money101: true,
  moneyhero: true,
  moneymax: true,
  singsaver: true,
};

/** Every slug and alias the brand claims as a CMS vertical path. */
export const getCmsVerticalUrls = (brand: BrandId): string[] =>
  Object.keys(VERTICAL_MAPPING_BY_BRAND[brand]);

export const CMS_CUSTOM_VERTICAL_MAPPING: Record<string, UrlPrefixEnum> = {
  promo: UrlPrefixEnum.UseCampaign,
};
export const CMS_CUSTOM_VERTICAL_URLS = Object.keys(
  CMS_CUSTOM_VERTICAL_MAPPING,
);

/**
 * Public path of the homepage: the locale prefix itself, `/` for a brand without one. A prefixed
 * brand's `/` is a redirect (MoneyHero: to `/zh`), so every link to the homepage goes through here.
 */
export const getHomePagePath = (langPrefix: string): string =>
  langPrefix || '/';

export function getCmsPageLink({
  pageType,
  langPrefix,
  slug,
  vertical,
  brand,
  urlPrefix,
}: CmsPageLinkProps): string {
  const verticalFragment = getUrlFragmentForVertical({
    brand,
    urlPrefix: urlPrefix ?? UrlPrefixEnum.UsePrimaryVertical,
    vertical,
  });
  const pageTypeFragment = PAGE_TYPE_MAPPINGS[pageType];

  const url = [
    // withoutPrefix ? undefined : CMS_PREFIX,
    verticalFragment,
    pageTypeFragment,
    slug,
  ]
    .filter(Boolean)
    .join('/');

  return `${langPrefix}/${url}`;
}

// todo cms-1622 add apply-v3
export const PAGE_TYPE_MAPPINGS: Record<PageType, string | undefined> = {
  article: 'blog',
  page: undefined,
  product: 'products',
  author: 'author',
  // tag archive lives under the blog namespace: /blog/tag/<slug>
  tag: 'blog/tag',
};

export const getUrlFragmentForVertical = ({
  brand,
  urlPrefix,
  vertical,
}: {
  vertical?: VerticalType;
  brand: BrandId;
  urlPrefix?: UrlPrefixEnum;
}) => {
  let verticalFragment: string | undefined;

  // Default to UsePrimaryVertical if not specified
  const effectiveUrlPrefix = urlPrefix ?? UrlPrefixEnum.UsePrimaryVertical;

  if (effectiveUrlPrefix === UrlPrefixEnum.UsePrimaryVertical) {
    if (!vertical) {
      return undefined;
    }
    verticalFragment = resolveVerticalRoute(vertical, brand)?.slug;
  } else {
    verticalFragment = Object.keys(CMS_CUSTOM_VERTICAL_MAPPING).find(
      (key) => CMS_CUSTOM_VERTICAL_MAPPING[key] === effectiveUrlPrefix,
    );
  }

  return verticalFragment;
};

export type CmsPageLinkProps = {
  pageType: PageType;
  vertical?: VerticalType;
  slug?: string;
  urlPrefix?: UrlPrefixEnum;
  // withoutPrefix?: boolean; // todo when cms prefix is removed, remove this param
  langPrefix: string; // do not confuse lang (en-hk) and langPrefix (/en),
  brand: BrandId;
};
